import { Component, type ErrorInfo, type ReactNode } from 'react'
import { WidgetFrame } from './WidgetFrame'

interface Props {
  title: string
  children: ReactNode
}

interface State {
  error: Error | null
  /** Bumped on retry so the children remount from scratch. */
  attempt: number
}

/**
 * Keeps one broken widget from taking the Overview down. Catches errors thrown
 * while a widget renders, and a widget script that fails to load (for example
 * after a deploy replaced it). Failed data requests are NOT handled here: those
 * are reported through WidgetFrame's own error state.
 */
export class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { error: null, attempt: 0 }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Dashboard widget "${this.props.title}" crashed:`, error, info.componentStack)
  }

  retry = () => this.setState((s) => ({ error: null, attempt: s.attempt + 1 }))

  render() {
    if (this.state.error) {
      return <WidgetFrame title={this.props.title} error={this.state.error} onRetry={this.retry} />
    }
    return <div key={this.state.attempt} className="contents">{this.props.children}</div>
  }
}
